import type { TypePolicies } from '@apollo/client';
import { mergeDeep } from '@apollo/client/utilities';

import { defaultTypePolicies } from '#src/__generated__/defaultTypePolicies.ts';
import { scalarTypePolicies } from '#src/__generated__/typePolicies.ts';
import { cardTypes } from '#src/lib/cardTypes.ts';
import {
  mergeArrayOfUnnormalizedObjects,
  replaceByIncoming,
} from '#src/lib/gql/index.ts';

/* eslint sort-keys: "error" */
export const typePolicies: TypePolicies = mergeDeep(
  defaultTypePolicies,
  {
    Age: {
      merge: true,
    },
    AnyCardInterface: {
      fields: {
        collectionScoringMatrix: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    AnyPlayerInterface: {
      fields: {
        cardSupply: { merge: mergeArrayOfUnnormalizedObjects },
        news: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    BaseballPlayer: {
      fields: {
        allAverageStats: { merge: mergeArrayOfUnnormalizedObjects },
        allTotalStats: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    BaseballSubmitCommonDraftResponse: {
      keyFields: false,
    },
    BasketballPlayerGameScore: {
      fields: {
        detailedScore: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    BoostLevel: {
      keyFields: false,
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
    Card: {
      fields: {
        availableCardBoosts: {
          merge: false,
        },
      },
      keyFields: ['slug'],
    },
    CardBoost: {
      keyFields: ['shopItem', ['id']],
    },
    CardCollection: {
      fields: {
        bonusThresholds: { merge: mergeArrayOfUnnormalizedObjects },
        scoringMatrix: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CardCollectionConnection: {
      fields: {
        competitionsCount: { merge: mergeArrayOfUnnormalizedObjects },
        raritiesCount: { merge: mergeArrayOfUnnormalizedObjects },
        seasonsCount: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CardCount: {
      merge: true,
    },
    CardPack: {
      fields: {
        sections: { merge: mergeArrayOfUnnormalizedObjects },
        slots: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CardPackSlot: {
      fields: {
        playingStatusOdds: { merge: mergeArrayOfUnnormalizedObjects },
        rankOdds: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CardPullConfig: {
      fields: {
        revealCosts: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CardShardsChest: {
      fields: {
        thresholds: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CardShardsChestThreshold: {
      fields: {
        draws: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CardShardsChestThresholdDraw: {
      fields: {
        probabilities: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CardStats: {
      fields: {
        highlightedStats: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CardsCountTask: {
      fields: {
        thresholds: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    Cart: {
      fields: {
        affectedCardCollections: {
          keyFields: ['id'],
          merge: mergeArrayOfUnnormalizedObjects,
        },
        mainCartItems: {
          keyFields: ['id'],
          merge: mergeArrayOfUnnormalizedObjects,
        },
        savedForLaterCartItems: {
          keyFields: ['id'],
          merge: mergeArrayOfUnnormalizedObjects,
        },
      },
    },
    CompleteCollectionsCountTask: {
      fields: {
        thresholds: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    CurrentUser: {
      fields: {
        baseballCurrentUserData: { merge: true },
        baseballUnclaimedBoxRewards: { merge: false },
        baseballUnclaimedCashRewards: { merge: false },
        baseballUnclaimedLineupRewards: { merge: false },
        cardShardsChests: {
          keyArgs: ['sport', 'rarities'],
          merge: replaceByIncoming,
        },
        claimablePacks: {
          merge: replaceByIncoming,
        },
        competitionCardCollections: { merge: mergeArrayOfUnnormalizedObjects },
        connectedOauths: {
          merge: replaceByIncoming,
        },
        inGameCurrencyBalances: { merge: mergeArrayOfUnnormalizedObjects },
        myCart: {
          merge: true,
        },
        myUnclaimedBoostedRewardConfigsByRarity: {
          merge: mergeArrayOfUnnormalizedObjects,
        },
        nbaUnclaimedCardRewards: { merge: false },
        nbaUnclaimedCashRewards: { merge: false },
        nbaUnclaimedConversionCreditRewards: { merge: false },
        nbaUnclaimedLineupRewards: { merge: false },
        refereeRewards: {
          merge: replaceByIncoming,
        },
        referrals: {
          merge: replaceByIncoming,
        },
        unclaimedActionRewards: {
          merge: replaceByIncoming,
        },
        unclaimedSo5Rewards: {
          keyArgs: ['sport'],
          merge: replaceByIncoming,
        },
        unopenedProbabilisticBundles: {
          keyArgs: ['sport', 'rarity', 'design'],
          merge: true,
        },
        userSettings: {
          merge: true,
        },
      },
      keyFields: ['slug'],
    },
    DailyActionTask: {
      fields: {
        thresholds: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    DecisivePlayerPickerTask: {
      fields: {
        alternativeRewardConfigs: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    FootballRoot: {
      keyFields: false,
      merge: true,
    },
    ForYouRoot: {
      keyFields: false,
      merge: true,
    },
    GameOfBaseball: {
      fields: {
        scoresByInning: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    GameWithPlayerGameScoresPreviewConnection: {
      fields: {
        nodes: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    LeaderboardRewardsConfig: {
      fields: {
        conditional: {
          merge: mergeArrayOfUnnormalizedObjects,
        },
        ranking: {
          merge: mergeArrayOfUnnormalizedObjects,
        },
      },
      keyFields: false,
    },
    League: {
      fields: {
        members: {
          keyArgs: false,
          merge: (existing: any[], incoming: any[], _a) => {
            const args = { limit: 100, offset: 0, ...(_a.args || {}) };
            const merged = [...(existing || [])];
            const { offset } = args;
            incoming.forEach((member, index) => {
              merged[offset + index] = member;
            });
            return merged;
          },
        },
      },
      keyFields: ['slug'],
    },
    LeagueLeaderboard: {
      fields: {
        lineups: {
          keyArgs: false,
          merge: (existing: any[], incoming: any[], _a) => {
            const args = { limit: 100, offset: 0, ...(_a.args || {}) };
            const merged = [...(existing || [])];
            const { offset } = args;
            incoming.forEach((member, index) => {
              merged[offset + index] = member;
            });
            return merged;
          },
        },
      },
      keyFields: ['leaderboard', ['slug'], 'league', ['slug']],
    },
    MangopayRoot: {
      fields: {
        bankAccountType: {
          keyArgs: ['countryCode'],
        },
        klarnaPayinConfig: { merge: mergeArrayOfUnnormalizedObjects },
        kycDocumentRequirements: { merge: mergeArrayOfUnnormalizedObjects },
        ownerRegionRequired: {
          keyArgs: ['countryCode'],
        },
      },
    },
    MarketplaceSportPreferences: {
      fields: {
        preferences: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    NBAPlayer: {
      fields: {
        allAverageStats: { merge: mergeArrayOfUnnormalizedObjects },
        allTotalStats: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    NotificationPreference: {
      keyFields: ['name'],
    },
    PickablePlayer: {
      keyFields: ['slug'],
    },
    Player: {
      fields: {
        allAverageStats: { merge: mergeArrayOfUnnormalizedObjects },
        allTotalStats: { merge: mergeArrayOfUnnormalizedObjects },
        cardSupply: { merge: mergeArrayOfUnnormalizedObjects },
      },
      keyFields: ['slug'],
    },
    PlayerGameScore: {
      fields: {
        allAroundStats: { merge: mergeArrayOfUnnormalizedObjects },
        negativeDecisiveStats: { merge: mergeArrayOfUnnormalizedObjects },
        positiveDecisiveStats: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    PlayerGameScoreInterface: {
      fields: {
        detailedScore: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    PlayerInjury: {
      merge: true,
    },
    ProbabilisticBundleConfig: {
      fields: {
        slots: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    ProbabilisticBundleSlotConfig: {
      fields: {
        cards: { merge: mergeArrayOfUnnormalizedObjects },
        deliverableItems: { merge: mergeArrayOfUnnormalizedObjects },
        rewardShopItems: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    ProbabilisticBundlesRewardsOverview: {
      fields: {
        rarities: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    ProgressionForRewardConfigThresholds: {
      fields: {
        thresholds: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    Query: {
      fields: {
        anyCard: {
          read(existing, { args, toReference, canRead }) {
            if (existing) return existing;
            if (!args?.slug) return undefined;

            const footballCardRef = toReference({
              __typename: cardTypes.FOOTBALL,
              slug: args.slug,
            });
            const baseballCardRef = toReference({
              __typename: cardTypes.BASEBALL,
              slug: args.slug,
            });
            const nbaCardRef = toReference({
              __typename: cardTypes.NBA,
              slug: args.slug,
            });

            return [footballCardRef, baseballCardRef, nbaCardRef].find(ref =>
              canRead(ref)
            );
          },
        },
        cardPullConfigs: { merge: mergeArrayOfUnnormalizedObjects },
        competitionGames: { merge: mergeArrayOfUnnormalizedObjects },
        searchPlayerFacetValues: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    Referral: {
      fields: {
        referrerRewards: {
          merge: replaceByIncoming,
        },
      },
    },
    ReferralPaginated: {
      keyFields: false,
    },
    RewardBoost: {
      keyFields: false,
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
    RewardMultiplierConfiguration: {
      fields: {
        cardBoosts: {
          merge: mergeArrayOfUnnormalizedObjects,
        },
      },
      keyFields: false,
    },
    RewardsOverview: {
      fields: {
        experiencesDetails: {
          read(value) {
            return value || [];
          },
        },
      },
      keyFields: false,
    },
    ScoringMatrix: {
      fields: {
        statCustomScorings: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    SealingConfig: {
      fields: {
        fullRarityVaultMatrix: { merge: mergeArrayOfUnnormalizedObjects },
      },
      keyFields: false,
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
    SearchCards: {
      fields: {
        facets: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    SearchFacet: {
      fields: {
        values: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    SearchPlayers: {
      fields: {
        facets: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    Season: {
      keyFields: ['startYear'],
    },
    SkinShopItem: {
      fields: {
        cardCollectionRequirements: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5AppearancePreview: {
      keyFields: false,
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
    So5Fixture: {
      fields: {
        myBoostedRewardConfigsByRarity: {
          merge: mergeArrayOfUnnormalizedObjects,
        },
      },
    },
    So5Leaderboard: {
      fields: {
        canCompose: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
        mySo5Lineups: {
          merge: mergeArrayOfUnnormalizedObjects,
        },
        possibleBenchSortingTypes: { merge: mergeArrayOfUnnormalizedObjects },
        rewardsConfig: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
        totalRewards: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
      },
      keyFields: ['slug'],
    },
    So5League: {
      fields: {
        mySo5LeaderboardContenders: { merge: replaceByIncoming },
      },
    },
    So5LineupImport: {
      fields: {
        appearances: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5LineupPreview: {
      fields: {
        appearances: { merge: mergeArrayOfUnnormalizedObjects },
        feedbackRules: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5NoCardRouteFixturePickConfig: {
      fields: {
        pickingSlots: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5Ranking: {
      fields: {
        eligibleRewards: {
          merge: mergeArrayOfUnnormalizedObjects,
        },
      },
    },
    So5Reward: {
      fields: {
        alternativeRewardConfigs: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5RewardConfig: {
      fields: {
        cards: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5RewardConfigLock: {
      fields: {
        lineupLiveFeedbacksRules: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5Root: {
      fields: {
        cardXpGradeThreshold: { merge: mergeArrayOfUnnormalizedObjects },
      },
      keyFields: false,
      merge: true,
    },
    So5RuleDescription: {
      fields: {
        items: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5RuleFeedback: {
      keyFields: ['ruleName'],
    },
    So5RuleInterface: {
      fields: {
        descriptions: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5Rules: {
      fields: {
        rarityLimits: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
      },
    },
    So5Score: {
      fields: {
        negativeDecisiveStats: { merge: mergeArrayOfUnnormalizedObjects },
        positiveDecisiveStats: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    So5SeasonBonusRule: {
      fields: {
        immuneCountByRarity: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    SocialPictureDerivatives: {
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
    SportSeason: {
      keyFields: ['name'],
    },
    TaskCampaign: {
      fields: {
        detailSections: { merge: mergeArrayOfUnnormalizedObjects },
      },
      keyFields: ['slug'],
    },
    TaskInterface: {
      fields: {
        alternativeRewardConfigs: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    TeamGameStatsInterface: {
      fields: {
        stats: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    ThresholdsStreakTask: {
      fields: {
        alternativeRewardConfigs: { merge: mergeArrayOfUnnormalizedObjects },
        thresholds: {
          merge: mergeArrayOfUnnormalizedObjects,
        },
      },
    },
    TokenAuction: {
      fields: {
        bidToEarnRewardConfigs: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    TokenRoot: {
      fields: {
        promotionalEvents: {
          keyFields: false,
          merge(
            existing: any[] | undefined,
            incoming: any[],
            { mergeObjects }
          ) {
            return incoming.map(currentValue => {
              const formerValue = existing?.find(
                e => e.slug === currentValue.slug
              );
              if (formerValue) {
                return mergeObjects(formerValue, currentValue);
              }
              return currentValue;
            });
          },
        },
      },
      merge: true,
    },
    User: {
      fields: {
        searchCardFacetValues: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    UserProfile: {
      fields: {
        marketplacePreferences: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    UserSettings: {
      fields: {
        notificationPreferences: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    UserWallet: {
      fields: {
        privateKeyRecoveryPayloads: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
    Watchlist: {
      fields: {
        searchPlayerFacetValues: { merge: mergeArrayOfUnnormalizedObjects },
      },
      keyFields: ['slug'],
    },
    WheelConfig: {
      fields: {
        bidToEarnRewardConfigs: { merge: mergeArrayOfUnnormalizedObjects },
      },
    },
  } as TypePolicies,
  scalarTypePolicies
);
